import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { CalendarDays, Keyboard, Plug, Settings2, SunMoon, Users, Zap } from "lucide-react";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { type SettingsSection, settingsPath, settingsRouteFromPathname } from "@/lib/settingsRoutes";
import { cn } from "@repo/blacksmith-ui/cn";
import { RoutePrefetchLink } from "../ui/route-prefetch-link";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "../ui/sidebar";
import { AppSidebarHeader, AppSidebarHeaderActions } from "../ui/app-header";
import { useCodesmithSidebarVariant } from "@/components/codesmith/useCachedSidebarRedesignEnabled";
const menuItemClassName = "group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1";
const menuButtonClassName = "h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 data-[active=true]:bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:data-[active=true]:bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white cursor-pointer";
type SectionNavItem = {
  name: string;
  section: SettingsSection;
  icon: React.ReactNode;
};

/**
 * Contextual sidebar for the settings routes. Sits to the right of the
 * app-switcher rail and links to the section pages under
 * `/[org]/settings`. Only the section page swaps on navigation; the settings
 * layout stays mounted.
 */
export default function SettingsSidebar() {
  const $ = _c(59);
  const router = useRouter();
  const {
    activeGithubOrg,
    codesmithEnabled
  } = useActiveGithubOrgForUserNonSuspense();
  const urlOrg = router.query.org;
  const currentOrg = typeof urlOrg === "string" ? urlOrg : activeGithubOrg;
  const redesign = useCodesmithSidebarVariant(currentOrg ?? null) === "redesign";
  const t0 = redesign ? "gap-0" : "gap-1";
  let t1;
  if ($[0] !== t0) {
    t1 = cn("flex flex-col", t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const menuClassName = t1;
  const t2 = redesign && "group-data-[state=expanded]:border-y-2 group-data-[state=expanded]:bg-clip-padding";
  let t3;
  if ($[2] !== t2) {
    t3 = cn(menuButtonClassName, t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const buttonClassName = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Settings2 className="size-[18px]" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t10;
  let t11;
  let t12;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== buttonClassName || $[6] !== codesmithEnabled || $[7] !== currentOrg || $[8] !== menuClassName || $[9] !== redesign || $[10] !== router.pathname) {
    const nav = [{
      name: "General",
      section: "general",
      icon: t4
    }, {
      name: "Appearance",
      section: "appearance",
      icon: <SunMoon className="size-[18px]" />
    }, {
      name: "Features",
      section: "features",
      icon: <Zap className="size-[18px]" />
    }, {
      name: "Integrations",
      section: "integrations",
      icon: <Plug className="size-[18px]" />
    }, {
      name: "Team",
      section: "team",
      icon: <Users className="size-[18px]" />
    }, ...(codesmithEnabled ? [{
      name: "[code]smith",
      section: "codesmith" as const,
      icon: <CodesmithIcon className="size-[18px]" />
    }] : [])];
    const activeSection = settingsRouteFromPathname(router.pathname)?.section ?? "general";
    T4 = ShadcnSidebar;
    t10 = "icon";
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = cn("left-[var(--app-switcher-rail-width,_48px)] transition-[left] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]", "[&_[data-slot=sidebar-inner]]:border-[#0c0c0d14] dark:[&_[data-slot=sidebar-inner]]:border-white/12");
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <SidebarHeader className="shrink-0 bg-sidebar p-0"><AppSidebarHeader title="Settings" actions={<AppSidebarHeaderActions />} collapsible={true} /></SidebarHeader>;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    T3 = SidebarContent;
    t9 = "flex min-h-[200px] flex-col gap-0 overflow-hidden bg-sidebar py-0 group-data-[state=collapsed]:px-0 group-data-[collapsible=icon]:overflow-hidden";
    T2 = SidebarGroup;
    const t13 = redesign ? "pt-0" : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
    if ($[26] !== t13) {
      t8 = cn("border-0 min-h-0 flex-1 overflow-y-auto group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1", t13);
      $[26] = t13;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    T1 = SidebarGroupContent;
    t7 = "flex flex-1 flex-col px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0";
    T0 = SidebarMenu;
    t5 = menuClassName;
    t6 = nav.map(item => {
      const href = settingsPath(currentOrg, item.section);
      return <SidebarMenuItem key={item.name} className={menuItemClassName}><SidebarMenuButton asChild={true} isActive={activeSection === item.section} tooltip={item.name} className={buttonClassName}><RoutePrefetchLink href={href} prefetch={currentOrg ? true : null} prefetchOnIntent={!!currentOrg} className="cursor-pointer"><div className="relative">{item.icon}</div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{item.name}</span></RoutePrefetchLink></SidebarMenuButton></SidebarMenuItem>;
    });
    $[5] = buttonClassName;
    $[6] = codesmithEnabled;
    $[7] = currentOrg;
    $[8] = menuClassName;
    $[9] = redesign;
    $[10] = router.pathname;
    $[11] = T0;
    $[12] = T1;
    $[13] = T2;
    $[14] = T3;
    $[15] = T4;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    T0 = $[11];
    T1 = $[12];
    T2 = $[13];
    T3 = $[14];
    T4 = $[15];
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t13;
  if ($[28] !== T0 || $[29] !== t5 || $[30] !== t6) {
    t13 = <T0 className={t5}>{t6}</T0>;
    $[28] = T0;
    $[29] = t5;
    $[30] = t6;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== T1 || $[33] !== t13 || $[34] !== t7) {
    t14 = <T1 className={t7}>{t13}</T1>;
    $[32] = T1;
    $[33] = t13;
    $[34] = t7;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== T2 || $[37] !== t14 || $[38] !== t8) {
    t15 = <T2 className={t8}>{t14}</T2>;
    $[36] = T2;
    $[37] = t14;
    $[38] = t8;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== T3 || $[41] !== t15 || $[42] !== t9) {
    t16 = <T3 className={t9}>{t15}</T3>;
    $[40] = T3;
    $[41] = t15;
    $[42] = t9;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <a href="https://www.blacksmith.sh/contact-our-team" target="_blank" rel="noopener noreferrer"><div className="relative"><CalendarDays className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Book a demo</span></a>;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== buttonClassName) {
    t18 = <SidebarMenuItem className={menuItemClassName}><SidebarMenuButton asChild={true} tooltip="Book a demo" className={buttonClassName}>{t17}</SidebarMenuButton></SidebarMenuItem>;
    $[45] = buttonClassName;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <SidebarMenuItem className={menuItemClassName}><SidebarMenuButton tooltip="Keyboard shortcuts" className={menuButtonClassName} onClick={_temp}><div className="relative"><Keyboard className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Keyboard shortcuts</span></SidebarMenuButton></SidebarMenuItem>;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== menuClassName || $[49] !== t18) {
    t20 = <SidebarFooter className="shrink-0 gap-0 p-0 group-data-[state=collapsed]:border-t-0"><SidebarGroup className="border-0 py-2 group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1"><SidebarGroupContent className="px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0"><SidebarMenu className={menuClassName}>{t18}{t19}</SidebarMenu></SidebarGroupContent></SidebarGroup></SidebarFooter>;
    $[48] = menuClassName;
    $[49] = t18;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SidebarRail />;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== T4 || $[53] !== t10 || $[54] !== t11 || $[55] !== t12 || $[56] !== t16 || $[57] !== t20) {
    t22 = <T4 collapsible={t10} className={t11}>{t12}{t16}{t20}{t21}</T4>;
    $[52] = T4;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
    $[56] = t16;
    $[57] = t20;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  return t22;
}
function _temp() {
  return document.dispatchEvent(new KeyboardEvent("keydown", {
    key: "?",
    bubbles: true,
    cancelable: true
  }));
}